import React from 'react';
import {
    Badge,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    InfoOutlined,
    WarningAmber,
} from '@mui/icons-material';
import {
    ChangeStatusIcon,
    CTableWrapper,
    DeleteIcon,
    EditIcon,
    OptionalDataIcon,
} from '../../../../../dum';
import {
    FIELD_LABELS,
    formatDate,
    formatFloat,
    formatRelativeDate,
    round,
    shortLabel,
} from '../../../../../../helpers';
import { PdfIcon } from '../../../../../smart';
import { OrderSalesPageQuery } from '../../../../../../services';

type SaleDoc = OrderSalesPageQuery['paginatedOrderSales']['docs'][number];

interface ISaleCard {
    orderSale: SaleDoc;
    onEdit: (orderSale: SaleDoc) => void;
    onDelete: (orderSale: SaleDoc) => void;
    onPrint: (orderSale: SaleDoc) => void;
    // Optional, role-gated actions. The icon only renders when its handler is
    // provided (the list block passes them only for the allowed roles), mirroring
    // the same actions in the card-list context menu.
    onChangeStatus?: (orderSale: SaleDoc) => void;
    onEditDetails?: (orderSale: SaleDoc) => void;
}

/**
 * Card body for one order sale inside the Ventas CCardList. Two header lines
 * (folio/estatus/fecha, then cliente/creado por/pedido), the products
 * sub-table, and a footer with totals + actions.
 */
export function SaleCard({
    orderSale,
    onEdit,
    onDelete,
    onPrint,
    onChangeStatus,
    onEditDetails,
}: ISaleCard) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const subBaseColor = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
    const subStripeColor = isDark ? 'rgba(59,130,246,0.16)' : '#dbeafe';

    const folio =
        orderSale.invoice_code === 0
            ? String(orderSale.order_code)
            : `${orderSale.order_code} F-${orderSale.invoice_code}`;
    // Saldo is measured against the net total (invoice minus Devolución
    // adjustments); a negative saldo means the client overpaid.
    const saldo = round(
        orderSale.net_total_with_tax - orderSale.transfer_receipts_total,
    );
    const isTransferComplete = saldo === 0;
    const hasReturns =
        round(orderSale.net_total_with_tax) !== round(orderSale.total_with_tax);

    // Payment-date hint next to Saldo: info icon (tooltip shows the date) when
    // a date is set, warning icon when one is still missing.
    const paymentDateIcon = orderSale.expected_payment_date ? (
        <Tooltip
            arrow
            title={`Fecha de pago: ${formatDate(
                orderSale.expected_payment_date,
            )}`}
        >
            <InfoOutlined
                fontSize={'small'}
                color={'info'}
                sx={{ verticalAlign: 'middle', ml: 0.5 }}
            />
        </Tooltip>
    ) : (
        <Tooltip arrow title={'Sin fecha de pago asignada'}>
            <WarningAmber
                fontSize={'small'}
                color={'warning'}
                sx={{ verticalAlign: 'middle', ml: 0.5 }}
            />
        </Tooltip>
    );

    return (
        <Box sx={{ opacity: orderSale.canceled ? 0.6 : 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 0.5,
                }}
            >
                <Typography variant={'subtitle2'}>#{folio}</Typography>
                {orderSale.receipt_type ? (
                    <Typography variant={'caption'} color={'text.secondary'}>
                        ({orderSale.receipt_type.name})
                    </Typography>
                ) : null}
                {orderSale.order_sale_status ? (
                    <Chip
                        size={'small'}
                        label={orderSale.order_sale_status.name}
                    />
                ) : null}
                {orderSale.canceled ? (
                    <Chip size={'small'} color={'error'} label={'Cancelada'} />
                ) : null}
                {orderSale.reconciliation_only ? (
                    <Chip
                        size={'small'}
                        color={'warning'}
                        variant={'outlined'}
                        label={'Solo conciliación'}
                    />
                ) : null}
                <Box sx={{ flex: 1 }} />
                <Typography variant={'body2'}>
                    {formatDate(orderSale.date)}
                    {orderSale.updated_at ? (
                        <Typography
                            component={'span'}
                            variant={'caption'}
                            color={'text.secondary'}
                            sx={{ ml: 0.5 }}
                        >
                            (act. {formatRelativeDate(orderSale.updated_at)}
                            {orderSale.updated_by
                                ? ` por ${orderSale.updated_by.fullname}`
                                : ''}
                            )
                        </Typography>
                    ) : null}
                </Typography>
            </Box>
            <Typography variant={'body2'} sx={{ mb: 1 }}>
                <b>{orderSale.account?.name}</b>
                {orderSale.order_request
                    ? ` · pedido #${orderSale.order_request.order_code}`
                    : ''}
            </Typography>
            {orderSale.notes ? (
                <Typography variant={'caption'} color={'text.secondary'}>
                    {orderSale.notes}
                </Typography>
            ) : null}
            <CTableWrapper
                padding={false}
                baseColor={subBaseColor}
                stripeColor={subStripeColor}
                items={[
                    {
                        title: 'Producto',
                        cellGrow: 60,
                        content: (osp) => osp.product?.compound_description,
                    },
                    // Each price sits beside the quantity it prices:
                    // Bultos | $ del bulto | Kilos | $ del kilo.
                    {
                        title: shortLabel(FIELD_LABELS.groups),
                        cellGrow: 20,
                        textAlign: 'right',
                        content: (osp) => formatFloat(osp.groups),
                    },
                    {
                        title: shortLabel(FIELD_LABELS.group_price),
                        cellGrow: 20,
                        textAlign: 'right',
                        content: (osp) => formatFloat(osp.group_price),
                    },
                    {
                        title: shortLabel(FIELD_LABELS.kilos),
                        cellGrow: 20,
                        textAlign: 'right',
                        content: (osp) => formatFloat(osp.kilos),
                    },
                    {
                        title: shortLabel(FIELD_LABELS.kilo_price),
                        cellGrow: 20,
                        textAlign: 'right',
                        content: (osp) => formatFloat(osp.kilo_price),
                    },
                    {
                        title: 'Total',
                        cellGrow: 20,
                        textAlign: 'right',
                        content: (osp) => formatFloat(osp.total),
                    },
                ]}
                data={orderSale.order_sale_products}
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 1,
                }}
            >
                <Typography variant={'body2'} color={'text.secondary'}>
                    Subtotal {formatFloat(orderSale.subtotal)} · Total{' '}
                    {formatFloat(orderSale.total_with_tax)}
                    {hasReturns &&
                        ` · Neto ${formatFloat(orderSale.net_total_with_tax)}`}{' '}
                    · Saldo{' '}
                    {isTransferComplete ? (
                        <Badge
                            variant={'dot'}
                            badgeContent={' '}
                            color={'success'}
                            sx={{ ml: 1, mr: 1 }}
                        />
                    ) : (
                        // Saldo = what's left to transfer (net total minus what
                        // has already been transferred), not the transferred amount.
                        formatFloat(saldo)
                    )}
                    {!isTransferComplete && paymentDateIcon}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton
                    size={'small'}
                    disabled={!orderSale.is_editable}
                    onClick={(e) => {
                        // The whole card is clickable (opens edit) — keep the
                        // buttons from double-firing through it.
                        e.stopPropagation();
                        onEdit(orderSale);
                    }}
                >
                    <EditIcon />
                </IconButton>
                {onEditDetails ? (
                    <Tooltip arrow title={'Editar datos opcionales'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEditDetails(orderSale);
                            }}
                        >
                            <OptionalDataIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                {onChangeStatus ? (
                    <Tooltip arrow title={'Cambiar status'}>
                        <IconButton
                            size={'small'}
                            onClick={(e) => {
                                e.stopPropagation();
                                onChangeStatus(orderSale);
                            }}
                        >
                            <ChangeStatusIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
                <IconButton
                    size={'small'}
                    disabled={!orderSale.is_deletable}
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(orderSale);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
                <IconButton
                    size={'small'}
                    onClick={(e) => {
                        e.stopPropagation();
                        onPrint(orderSale);
                    }}
                >
                    <PdfIcon />
                </IconButton>
            </Box>
        </Box>
    );
}
